import { Link as LinkIcon, Signal, ShieldHalf, Settings2 } from "lucide-react";
import type { MCPServer, LLMSettings } from "@/types";
import { StatusPill } from "./StatusPill";
import MCPChatIcon from "public/chatmcp-white.svg";
import { ModelSelector } from "./ModelSelector";

interface ChatHeaderProps {
  contact: MCPServer;
  isStreaming?: boolean;
  onOpenSettings?: (contact: MCPServer) => void;
  onOpenAccount?: () => void;
  llmSettings?: LLMSettings;
  onModelChange?: (model: string, provider: LLMSettings["provider"]) => void;
}

export const ChatHeader = ({ contact, isStreaming, onOpenSettings, onOpenAccount, llmSettings, onModelChange }: ChatHeaderProps) => (
  <div className="relative flex flex-col gap-2 rounded-2xl border border-white/60 bg-white/70 px-3 py-2.5 sm:px-4 sm:py-3 shadow-sm dark:bg-white/5">
    <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 sm:gap-3">
      {/* Logo - smaller on mobile */}
      {onOpenSettings && (
        <button
          type="button"
          onClick={() => onOpenSettings(contact)}
          className="flex-shrink-0"
        >
          <div
            className={`h-8 w-8 sm:h-12 sm:w-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${contact.color ?? "from-primary to-sky-500"} text-white shadow-md grid place-items-center`}
          >
            <img src={MCPChatIcon} alt="ChatMCP Logo" className="h-6 w-6 sm:h-10 sm:w-10" />
          </div>
        </button>
      )}

      {/* Server info - flexible width, 2 lines */}
      {onOpenSettings && (
        <button
          type="button"
          onClick={() => onOpenSettings(contact)}
          className="space-y-0.5 min-w-0 text-left"
        >
          <p className="inline-flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-lg font-semibold text-foreground">
            <span className="truncate">{contact.name}</span>
          </p>
          <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-muted-foreground">
            <LinkIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
            <span className="truncate font-mono text-[9px] sm:text-[11px]">{contact.url}</span>
          </div>
        </button>
      )}

      {/* Model Selector */}
      {llmSettings && onModelChange && (
        <div className="flex-shrink-0">
          <ModelSelector
            currentModel={llmSettings.model}
            currentProvider={llmSettings.provider}
            onModelChange={onModelChange}
            onOpenAccount={onOpenAccount}
          />
        </div>
      )}

      {/* Status + Settings - icon only on mobile, full pill on desktop */}
      <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
        <StatusPill status={contact.status} />
        {isStreaming ? <span className="hidden sm:inline-flex pill bg-primary/10 text-primary">Streaming</span> : null}
        {isStreaming ? <Signal className="sm:hidden h-4 w-4 text-primary animate-pulse" /> : null}
        {/* Settings Button */}
        {onOpenSettings && (
          <button
            type="button"
            onClick={() => onOpenSettings(contact)}
            className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Open settings"
            title="Server Settings"
          >
            <Settings2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
    {/* Helper text - hidden on mobile */}
    <div className="hidden sm:flex items-center gap-3 text-xs text-muted-foreground">
      <ShieldHalf className="h-4 w-4 text-primary" />
      <span>Tap to edit this MCP server.</span>
    </div>
  </div>
);
